import {
  ErrorRule,
  RuleMatch,
  RuleAction,
  NodeErrorHandling,
  NamedRuleSet,
} from '../types';
import { evaluateBodyPredicate } from './dataExtraction';

// The two implicit engine defaults (runtime §4.1) — never stored, shown to the
// author as a read-only footer so the full evaluation order is visible.
export const IMPLICIT_DEFAULTS: string[] = [
  'transient → retry ∞',
  'else → throw "{surfaced code}"',
];

// ---- Variable-name + status-token helpers ----

const VAR_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
export const isValidVariableName = (name: string): boolean => VAR_NAME_RE.test(name);

// SQLSTATE: 5 alphanumerics, allowed as an exact code only.
const SQLSTATE_RE = /^[0-9A-Za-z]{5}$/;
const RANGE_RE = /^(\d+)-(\d+)$/;

export interface StatusTokenInfo {
  valid: boolean;
  kind: 'int' | 'range' | 'sqlstate' | 'invalid';
  message?: string;
}

export const classifyStatusToken = (raw: string | number): StatusTokenInfo => {
  const token = String(raw).trim();
  if (token === '') return { valid: false, kind: 'invalid', message: 'Empty code' };
  if (/^\d+$/.test(token)) return { valid: true, kind: 'int' };
  const range = RANGE_RE.exec(token);
  if (range) {
    const lo = Number(range[1]);
    const hi = Number(range[2]);
    if (lo < hi) return { valid: true, kind: 'range' };
    return { valid: false, kind: 'invalid', message: `Range ${token} requires lo < hi` };
  }
  if (SQLSTATE_RE.test(token)) return { valid: true, kind: 'sqlstate' };
  return { valid: false, kind: 'invalid', message: `'${token}' is not a code, range, or SQLSTATE` };
};

// Does an observed status code satisfy one of the configured tokens? Used by
// the live test panel (§6) — an authoring approximation of executor matching.
export const statusMatches = (observed: string | number, tokens?: (string | number)[]): boolean => {
  if (!tokens || tokens.length === 0) return false;
  const obsStr = String(observed).trim();
  const obsNum = Number(obsStr);
  return tokens.some((t) => {
    const token = String(t).trim();
    if (token === obsStr) return true;
    const range = RANGE_RE.exec(token);
    if (range && !isNaN(obsNum)) {
      return obsNum >= Number(range[1]) && obsNum <= Number(range[2]);
    }
    return false;
  });
};

// ---- Human-readable summaries (collapsed cards, preview, library list) ----

export const summarizeMatch = (match: RuleMatch): string => {
  const parts: string[] = [];
  if (match.transient) parts.push('transient');
  if (match.statusCodes && match.statusCodes.length) parts.push(`status ${match.statusCodes.join(',')}`);
  if (match.exceptionTypes && match.exceptionTypes.length) parts.push(`type ${match.exceptionTypes.join(',')}`);
  if (match.bodyPredicate) {
    const { kind, expression, op, value } = match.bodyPredicate;
    parts.push(`body ${kind} ${expression} ${op}${value ? ` "${value}"` : ''}`);
  }
  return parts.length ? parts.join(' AND ') : 'any error';
};

export const summarizeAction = (action: RuleAction): string => {
  switch (action.disposition) {
    case 'retry': {
      const n = action.maxAttempts === 0 ? '∞' : String(action.maxAttempts);
      if (action.maxAttempts > 0 && action.onExhausted) {
        const oe = action.onExhausted;
        const tail = oe.disposition === 'throw'
          ? `throw "${oe.errorCode}"`
          : `ignore${oe.captureVariable ? ` (capture ${oe.captureVariable})` : ''}`;
        return `retry ${n}, on-exhausted → ${tail}`;
      }
      return `retry ${n}`;
    }
    case 'ignore':
      return `ignore${action.captureVariable ? ` (capture ${action.captureVariable})` : ''}`;
    case 'throw':
      return `throw "${action.errorCode}"`;
    default:
      return '';
  }
};

export const summarizeRule = (rule: ErrorRule): { when: string; then: string } => ({
  when: summarizeMatch(rule.match),
  then: summarizeAction(rule.action),
});

// ---- Content validation (shared by validateDiagram, the tab, the library) ----

export interface RuleSetIssue {
  ruleIndex: number; // -1 = ruleset-level
  severity: 'error' | 'warning';
  message: string;
}

export const validateRuleSet = (rules: ErrorRule[]): RuleSetIssue[] => {
  const issues: RuleSetIssue[] = [];
  if (!Array.isArray(rules)) return issues;

  rules.forEach((rule, i) => {
    const match = rule?.match || {};
    const action = rule?.action;

    // wildcard-above-others: an empty match placed before later rules shadows them.
    const isWildcard = !match.transient
      && !(match.statusCodes && match.statusCodes.length)
      && !(match.exceptionTypes && match.exceptionTypes.length)
      && !match.bodyPredicate;
    if (isWildcard && i < rules.length - 1) {
      issues.push({ ruleIndex: i, severity: 'warning', message: 'Wildcard match makes the rules below it unreachable.' });
    }

    // statusCodes tokens
    (match.statusCodes || []).forEach((code) => {
      const info = classifyStatusToken(code);
      if (!info.valid) issues.push({ ruleIndex: i, severity: 'error', message: info.message || `Invalid status code '${code}'.` });
    });

    // bodyPredicate
    if (match.bodyPredicate) {
      const bp = match.bodyPredicate;
      if (!bp.expression || !bp.expression.trim()) {
        issues.push({ ruleIndex: i, severity: 'error', message: 'Body predicate expression is required.' });
      }
      if ((bp.op === 'equals' || bp.op === 'matches') && (bp.value === undefined || bp.value === '')) {
        issues.push({ ruleIndex: i, severity: 'error', message: `Body predicate "${bp.op}" requires a value.` });
      }
      if (bp.kind === 'regex' && bp.expression) {
        try { new RegExp(bp.expression); } catch (e) {
          issues.push({ ruleIndex: i, severity: 'error', message: `Body predicate regex does not compile: ${(e as Error).message}` });
        }
      }
      if (bp.op === 'matches' && bp.value) {
        try { new RegExp(bp.value); } catch (e) {
          issues.push({ ruleIndex: i, severity: 'error', message: `Body predicate value regex does not compile: ${(e as Error).message}` });
        }
      }
    }

    if (!action) {
      issues.push({ ruleIndex: i, severity: 'error', message: 'Rule has no action.' });
      return;
    }

    if (action.disposition === 'throw') {
      if (!action.errorCode || !action.errorCode.trim()) {
        issues.push({ ruleIndex: i, severity: 'error', message: 'Throw requires an error code.' });
      }
    } else if (action.disposition === 'ignore') {
      if (action.captureVariable && !isValidVariableName(action.captureVariable)) {
        issues.push({ ruleIndex: i, severity: 'error', message: `Capture variable "${action.captureVariable}" is not a valid name.` });
      }
    } else if (action.disposition === 'retry') {
      if (typeof action.maxAttempts !== 'number' || action.maxAttempts < 0) {
        issues.push({ ruleIndex: i, severity: 'error', message: 'Retry max attempts must be ≥ 0.' });
      }
      if (action.maxAttempts > 0) {
        if (!action.onExhausted) {
          issues.push({ ruleIndex: i, severity: 'error', message: 'A bounded retry requires an on-exhausted action.' });
        } else if (action.onExhausted.disposition === 'throw') {
          if (!action.onExhausted.errorCode || !action.onExhausted.errorCode.trim()) {
            issues.push({ ruleIndex: i, severity: 'error', message: 'On-exhausted throw requires an error code.' });
          }
        } else if (action.onExhausted.disposition === 'ignore') {
          if (action.onExhausted.captureVariable && !isValidVariableName(action.onExhausted.captureVariable)) {
            issues.push({ ruleIndex: i, severity: 'error', message: `On-exhausted capture variable "${action.onExhausted.captureVariable}" is not a valid name.` });
          }
        }
      }
      if (action.backoff) {
        const b = action.backoff;
        if (!(b.baseMs > 0)) issues.push({ ruleIndex: i, severity: 'error', message: 'Backoff baseMs must be > 0.' });
        if (!(b.factor >= 1)) issues.push({ ruleIndex: i, severity: 'error', message: 'Backoff factor must be ≥ 1.' });
        if (!(b.jitter >= 0 && b.jitter < 1)) issues.push({ ruleIndex: i, severity: 'error', message: 'Backoff jitter must be in [0,1).' });
        if (!(b.capMs > 0)) issues.push({ ruleIndex: i, severity: 'error', message: 'Backoff capMs must be > 0.' });
      }
    }
  });

  return issues;
};

// ---- Live evaluation for the test panel (§6) ----

export interface SampleResponse {
  statusCode?: string | number;
  transient?: boolean;
  exceptionType?: string;
  body?: string;
}

export interface EvaluationResult {
  matchedIndex: number | null; // null = falls through to defaults
  rule?: ErrorRule;
  outcome: string;             // summarized action, or default behaviour text
  predicateError?: string;     // surfaced from a body predicate that could not evaluate
}

const exceptionMatches = (observed: string | undefined, patterns?: string[]): boolean => {
  if (!patterns || patterns.length === 0) return false;
  if (!observed) return false;
  return patterns.some((p) => {
    // support a leading/trailing "*" wildcard (e.g. "*.NotFound")
    if (p.includes('*')) {
      const esc = p.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
      try { return new RegExp(`^${esc}$`).test(observed); } catch { return false; }
    }
    return p === observed || observed.endsWith(`.${p}`);
  });
};

export const evaluateRuleSet = (rules: ErrorRule[], sample: SampleResponse): EvaluationResult => {
  let predicateError: string | undefined;
  for (let i = 0; i < rules.length; i++) {
    const m = rules[i].match || {};
    let ok = true;
    let touched = false;

    if (m.transient !== undefined) { touched = true; if (!!sample.transient !== m.transient) ok = false; }
    if (ok && m.statusCodes && m.statusCodes.length) {
      touched = true;
      if (sample.statusCode === undefined || sample.statusCode === '' || !statusMatches(sample.statusCode, m.statusCodes)) ok = false;
    }
    if (ok && m.exceptionTypes && m.exceptionTypes.length) {
      touched = true;
      if (!exceptionMatches(sample.exceptionType, m.exceptionTypes)) ok = false;
    }
    if (ok && m.bodyPredicate) {
      touched = true;
      const res = evaluateBodyPredicate(sample.body, m.bodyPredicate);
      if (res.error) predicateError = res.error;
      if (!res.matched) ok = false;
    }

    // empty match {} = wildcard (always matches)
    if (!touched) ok = true;

    if (ok) {
      return {
        matchedIndex: i,
        rule: rules[i],
        outcome: summarizeAction(rules[i].action),
        predicateError,
      };
    }
  }
  return {
    matchedIndex: null,
    outcome: sample.transient ? 'default → retry ∞' : 'default → throw "{surfaced code}"',
    predicateError,
  };
};

// Collect the distinct error codes a ruleset can THROW — from a `throw`
// disposition or a bounded retry's `onExhausted: throw`. Used to check each
// thrown code is caught by an Error Boundary Event (editor/simulator/skills).
export const collectThrownCodes = (rules: ErrorRule[]): string[] => {
  const codes = new Set<string>();
  for (const rule of rules || []) {
    const a = rule?.action;
    if (!a) continue;
    if (a.disposition === 'throw' && a.errorCode) codes.add(a.errorCode);
    if (a.disposition === 'retry' && a.maxAttempts > 0 && a.onExhausted?.disposition === 'throw' && a.onExhausted.errorCode) {
      codes.add(a.onExhausted.errorCode);
    }
  }
  return [...codes];
};

// ---- Node binding helpers ----

export const getNodeRules = (eh: NodeErrorHandling | undefined): ErrorRule[] =>
  (eh && Array.isArray(eh.rules)) ? eh.rules : [];

export const getNodeRef = (eh: NodeErrorHandling | undefined): string | undefined =>
  eh && '$ref' in eh ? eh.$ref : undefined;

// Count how many nodes (across all processes) bind to a given library ruleset id.
export const countRuleSetUsage = (
  processes: { nodes: { data?: { errorHandling?: NodeErrorHandling } }[] }[],
  id: string,
): number => {
  let count = 0;
  for (const proc of processes) {
    for (const node of proc.nodes) {
      if (getNodeRef(node.data?.errorHandling) === id) count++;
    }
  }
  return count;
};

// Re-inline each bound node's `rules` from the current library (export step).
// Returns a new errorHandling or the original if nothing to refresh.
export const reinlineFromLibrary = (
  eh: NodeErrorHandling,
  library: NamedRuleSet[],
): NodeErrorHandling => {
  const ref = getNodeRef(eh);
  if (!ref) return eh;
  const entry = library.find((r) => r.id === ref);
  if (!entry) return eh; // dangling ref — keep last-inlined rules (validation flags it)
  return { $ref: ref, rules: entry.rules };
};
